import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Spin, message, Tooltip, Modal } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../services/api';
import RateHistoryButton from '../components/RateHistoryButton';

const GOLD = '#C8922A';
const BD = '#D8D2C8';
const BD2 = '#EAE6DF';
const BG = '#FAF8F5';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#C0392B';
const GRN = '#2E7D4F';

const CAT_ICON = { watch: '⌚', jewelry: '💍', jewel: '💍', bag: '👜', art: '🎨' };

function mapOrder(o) {
  const isPaid    = !!(o.deducted_at && !o.deduct_error);
  const isOverdue = !!(o.overdue_at);
  const isPending = !!(o.deduct_error && o.payment_requested_at && !o.overdue_at);
  const totalCny   = Number(o.total_amount_cny   || 0);
  const depositCny = Number(o.deposit_freeze_cny || 0);
  const balanceCny = Math.max(0, totalCny - depositCny);
  const hammerJpy  = Number(o.won_price_jpy      || 0);
  const hammerCny  = Number(o.item_amount_cny    || 0);
  const jpyRate    = hammerJpy > 0 && hammerCny > 0
    ? parseFloat((hammerCny / hammerJpy).toFixed(5)) : 0.043;
  const serviceFee    = Number(o.service_fee_cny    || 0);
  const inspectionFee = Number(o.inspection_fee_cny || 0);
  const logisticsFee  = Number(o.shipping_fee_cny   || 0);
  const serviceFeeRate = hammerCny > 0 && serviceFee > 0
    ? parseFloat((serviceFee / hammerCny).toFixed(4)) : 0.09;
  const sessionLabel = o.session_name
    ? `${o.house_name} · ${o.session_name}`
    : (o.house_name || '--');
  const endAt = o.session_end_at ? dayjs(o.session_end_at).format('YYYY-MM-DD') : '';
  // 付款截止 = 场次结束 + 3天（保留完整时间以便精确倒计时）
  const payDeadlineDj = o.session_end_at ? dayjs(o.session_end_at).add(3, 'day') : null;
  const payDeadline   = payDeadlineDj ? payDeadlineDj.format('YYYY-MM-DD HH:mm') : '';
  // 重新上架截止 = 付款截止 + 1个月
  const reAuctionDeadline = payDeadlineDj
    ? payDeadlineDj.add(1, 'month').format('YYYY年M月D日') : null;
  return {
    id         : o.order_no || o.id,
    orderId    : o.id,
    mandateId  : o.mandate_id || '',
    sessionId  : o.session_id,
    sessionName: sessionLabel,
    endAt,
    lotTitle   : o.title_zh || o.title_ja || '--',
    icon       : CAT_ICON[o.category] || '📦',
    hammerJpy, jpyRate, hammerCny,
    serviceFeeRate, serviceFee, inspectionFee, logisticsFee,
    totalCny, depositCny, balanceCny,
    payDeadline,
    reAuctionDeadline,
    // 'paid' | 'overdue' | 'pending_confirmation' | 'unpaid'
    status: isPaid ? 'paid' : isOverdue ? 'overdue' : isPending ? 'pending_confirmation' : 'unpaid',
    paidAt: isPaid ? dayjs(o.deducted_at).format('YYYY-MM-DD HH:mm') : null,
    requestedAt: o.payment_requested_at
      ? dayjs(o.payment_requested_at).format('YYYY-MM-DD HH:mm') : null,
    overdueAt: o.overdue_at ? dayjs(o.overdue_at).format('YYYY-MM-DD HH:mm') : null,
  };
}

function formatAmount(val) {
  if (val == null) return '--';
  return '¥' + Number(val).toLocaleString();
}

export default function MyBill() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // 通过 navigate('/bills', { state: { focusOrderId } }) 跳转过来时，自动定位到该订单
  const focusOrderId = location.state?.focusOrderId || null;
  const [statusFilter, setStatusFilter] = useState(focusOrderId ? 'unpaid' : 'all');
  const [sessionFilter, setSessionFilter] = useState('');
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const focusRef = useRef(null);

  // 焦点订单 → 加载完后自动 scroll + 高亮
  useEffect(() => {
    if (!focusOrderId || loading) return;
    if (focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, focusOrderId]);

  useEffect(() => {
    const fetchBills = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/orders', { params: { limit: 200 } });
        setBills((data.data || data || []).map(mapOrder));
      } catch (err) {
        console.error('Failed to load bills:', err);
        message.error(t('common.failed'));
        setBills([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBills();
  }, []);

  // 从真实订单数据中提取场次选项（按 sessionId 去重）
  const sessionOptions = useMemo(() => {
    const map = new Map();
    bills.forEach(b => {
      if (b.sessionId != null && !map.has(b.sessionId)) {
        map.set(b.sessionId, b.sessionName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [bills]);

  const filtered = bills.filter(b => {
    // "待付款" tab 包含 unpaid 和 pending_confirmation
    if (statusFilter === 'unpaid' && b.status === 'paid') return false;
    if (statusFilter === 'paid'   && b.status !== 'paid') return false;
    if (sessionFilter && String(b.sessionId) !== sessionFilter) return false;
    return true;
  });

  const handleRequestPayment = async (orderId) => {
    try {
      // 1) 先查余额是否够支付
      const { data: chk } = await api.post(`/orders/${orderId}/check-balance`);
      if (chk.already_paid) {
        message.info(t('bill.detail'));
        return;
      }
      const totalFmt = `¥${Number(chk.total).toFixed(2)}`;
      const balanceFmt = `¥${Number(chk.balance).toFixed(2)}`;
      const frozenFmt  = `¥${Number(chk.frozen).toFixed(2)}`;

      if (!chk.can_pay) {
        // 余额不足 → 引导充值
        const shortageFmt = `¥${Number(chk.shortage).toFixed(2)}`;
        const usableFmt   = `¥${(Number(chk.balance) + Number(chk.frozen)).toFixed(2)}`;
        Modal.confirm({
          title: t('deposit.title'),
          content: (
            <div style={{ lineHeight: 1.8 }}>
              <div>{t('bill.amount')}：<strong>{totalFmt}</strong></div>
              <div>{t('deposit.available')}：{balanceFmt}</div>
              <div>{t('deposit.frozen')}：{frozenFmt}</div>
              <div style={{ borderTop: '1px dashed #ddd', marginTop: 6, paddingTop: 6 }}>
                合计可支付：<strong>{usableFmt}</strong>
              </div>
              <div style={{ color: '#C0392B', marginTop: 8 }}>
                还需充值：<strong>{shortageFmt}</strong>
              </div>
              <div style={{ marginTop: 12, color: '#666', fontSize: 12 }}>
                确认后将跳转至充值页面。
              </div>
            </div>
          ),
          okText: t('deposit.recharge'),
          cancelText: t('common.cancel'),
          onOk: () => navigate('/deposit'),
        });
        return;
      }

      // 2) 余额够 → 二次确认 → 调扣款 API
      const usableFmt = `¥${(Number(chk.balance) + Number(chk.frozen)).toFixed(2)}`;
      Modal.confirm({
        title: t('payment.payNow'),
        content: (
          <div style={{ lineHeight: 1.8 }}>
            <div>应付金额：<strong>{totalFmt}</strong></div>
            <div>可用余额：{balanceFmt}</div>
            <div>本单已冻结：{frozenFmt}（支付时自动抵扣）</div>
            <div style={{ borderTop: '1px dashed #ddd', marginTop: 6, paddingTop: 6 }}>
              合计可支付：<strong>{usableFmt}</strong>
            </div>
            <div style={{ marginTop: 12, color: '#666', fontSize: 12 }}>
              支付后等待管理员确认。
            </div>
          </div>
        ),
        okText: t('payment.payNow'),
        cancelText: t('common.cancel'),
        onOk: async () => {
          try {
            await api.post(`/orders/${orderId}/pay-from-balance`);
            setBills(prev => prev.map(b =>
              b.orderId === orderId
                ? { ...b, status: 'pending_confirmation', requestedAt: dayjs().format('YYYY-MM-DD HH:mm') }
                : b
            ));
            message.success(t('payment.paySuccess'));
          } catch (err) {
            message.error(err.response?.data?.error || t('payment.payFailed'));
          }
        },
      });
    } catch (err) {
      message.error(err.response?.data?.error || t('common.failed'));
    }
  };

  const totalCount   = bills.length;
  const unpaidBills  = bills.filter(b => b.status === 'unpaid');
  const pendingBills = bills.filter(b => b.status === 'pending_confirmation');
  const overdueBills = bills.filter(b => b.status === 'overdue');
  const paidBills    = bills.filter(b => b.status === 'paid');
  const unpaidTotal  = [...unpaidBills, ...pendingBills, ...overdueBills]
    .reduce((s, b) => s + b.balanceCny, 0);
  const paidTotal    = paidBills.reduce((s, b) => s + b.totalCny, 0);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px 0', background: BG, minHeight: '100vh' }}><Spin size="large" /></div>;
  }

  const filterTabs = [
    { key: 'all', label: t('common.all') },
    { key: 'unpaid', label: t('orders.statusPending') },
    { key: 'paid', label: t('orders.statusPaid') },
  ];

  return (
    <div style={{ background: BG, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${BD2}`, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <span
          style={{ cursor: 'pointer', fontSize: 18, color: INK3 }}
          onClick={() => navigate(-1)}
        >←</span>
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: INK }}>{t('bill.title')}</div>
          <div style={{ fontSize: 12, color: INK4, marginTop: 2 }}>{t('bill.detail')}</div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 24px' }}>
        {/* Filter tabs + session select */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 22, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: INK3 }}>{t('common.search')}：</span>
          <div style={{ display: 'flex', background: '#EDEAE3', borderRadius: 4, padding: 3, gap: 2 }}>
            {filterTabs.map(t => (
              <div
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                style={{
                  padding: '6px 16px', fontSize: 13, cursor: 'pointer', borderRadius: 3,
                  color: statusFilter === t.key ? INK : INK3,
                  fontWeight: statusFilter === t.key ? 500 : 400,
                  background: statusFilter === t.key ? '#fff' : 'transparent',
                  boxShadow: statusFilter === t.key ? '0 1px 4px rgba(26,22,18,.08)' : 'none',
                }}
              >{t.label}</div>
            ))}
          </div>
          <select
            value={sessionFilter}
            onChange={e => setSessionFilter(e.target.value)}
            style={{
              padding: '7px 12px', border: `1px solid ${BD}`, borderRadius: 3,
              fontSize: 13, background: '#fff', color: INK, outline: 'none', cursor: 'pointer',
            }}
          >
            <option value="">{t('common.all')}</option>
            {sessionOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        {/* Stats strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 22 }}>
          {[
            { val: totalCount,       label: t('bill.title'),  color: INK },
            { val: unpaidBills.length,  label: t('orders.statusPending'),    color: RED },
            { val: pendingBills.length, label: t('bill.detail'),    color: '#D97706' },
            { val: overdueBills.length, label: t('bill.expense'),      color: '#991B1B' },
            { val: paidBills.length,    label: t('orders.statusPaid'),    color: GRN },
          ].map((k, i) => (
            <div key={i} style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4, padding: '12px 10px', textAlign: 'center', boxShadow: '0 2px 12px rgba(26,22,18,.08)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: k.color }}>{k.val}</div>
              <div style={{ fontSize: 10, color: INK3, marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Unpaid / overdue banner */}
        {(unpaidBills.length > 0 || pendingBills.length > 0 || overdueBills.length > 0) && (
          <div style={{
            background: overdueBills.length > 0 ? '#7F1D1D' : INK,
            borderRadius: 4, padding: '14px 18px', marginBottom: 18,
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 22 }}>{overdueBills.length > 0 ? '🚨' : '⏰'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 3 }}>{t('bill.title')}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)' }}>
                {[
                  unpaidBills.length  > 0 && `${unpaidBills.length} 笔待付款`,
                  pendingBills.length > 0 && `${pendingBills.length} 笔待运营确认`,
                  overdueBills.length > 0 && `${overdueBills.length} 笔已滞纳`,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: '#F0C060' }}>
              {formatAmount(unpaidTotal)}
            </div>
          </div>
        )}

        {/* Bill list */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: INK4 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div>{t('common.noData')}</div>
            <div
              onClick={() => navigate('/mandates')}
              style={{
                display: 'inline-block', marginTop: 12, padding: '10px 24px',
                background: GOLD, color: '#fff', borderRadius: 4, fontSize: 13, cursor: 'pointer',
              }}
            >{t('mandates.title')} →</div>
          </div>
        ) : (
          filtered.map(b => (
            <BillCard
              key={b.id}
              bill={b}
              navigate={navigate}
              onRequestPayment={handleRequestPayment}
              isFocused={focusOrderId && (b.orderId === focusOrderId || b.id === focusOrderId)}
              focusRef={focusOrderId && (b.orderId === focusOrderId || b.id === focusOrderId) ? focusRef : null}
            />
          ))
        )}
      </div>
    </div>
  );
}

const AMB       = '#D97706';
const OVERDUE_C = '#991B1B'; // 滞纳深红

function BillCard({ bill: b, navigate, onRequestPayment, isFocused = false, focusRef = null }) {
  const { t } = useTranslation();
  const [paying, setPaying] = useState(false);

  // 剩余时间精确计算（毫秒）
  const deadlineMs   = b.payDeadline ? (new Date(b.payDeadline) - new Date()) : null;
  const deadlineDays = deadlineMs != null ? Math.floor(deadlineMs / 86400000) : 99;
  const deadlineHrs  = deadlineMs != null ? Math.floor((deadlineMs % 86400000) / 3600000) : 0;
  const urgent       = b.status === 'unpaid' && deadlineMs != null && deadlineMs > 0 && deadlineDays <= 2;

  const statusStyle =
    b.status === 'paid'
      ? { background: 'rgba(46,125,79,.10)',  borderColor: 'rgba(46,125,79,.2)',   color: GRN }
    : b.status === 'overdue'
      ? { background: 'rgba(153,27,27,.10)',  borderColor: 'rgba(153,27,27,.25)',  color: OVERDUE_C }
    : b.status === 'pending_confirmation'
      ? { background: 'rgba(217,119,6,.08)',  borderColor: 'rgba(217,119,6,.2)',   color: AMB }
    : urgent
      ? { background: 'rgba(192,57,43,.08)',  borderColor: 'rgba(192,57,43,.2)',   color: RED }
      : { background: 'rgba(100,100,100,.06)', borderColor: 'rgba(100,100,100,.15)', color: INK3 };

  // Badge 节点（含 tooltip）
  const urgentTooltip = (
    <div style={{ fontSize: 12, lineHeight: 1.7 }}>
      <div>⏱ 距支付截止还有 <b>{deadlineDays}天{deadlineHrs}小时</b></div>
      <div style={{ marginTop: 4, opacity: .85 }}>
        截止前未付款，将自动从您的余额中扣除保证金 <b>{formatAmount(b.depositCny)}</b>，不予返还
      </div>
      {b.reAuctionDeadline && (
        <div style={{ marginTop: 4, opacity: .85 }}>
          若 <b>{b.reAuctionDeadline}</b> 前仍未补足，该订单将被取消，该拍品将重新上架拍卖
        </div>
      )}
    </div>
  );
  const overdueTooltip = (
    <div style={{ fontSize: 12, lineHeight: 1.7 }}>
      <div>保证金已从您的余额中扣除，需补足余额 <b>{formatAmount(b.balanceCny)}</b> 及滞纳金后方可交付</div>
      {b.reAuctionDeadline && (
        <div style={{ marginTop: 4, opacity: .85 }}>
          若 <b>{b.reAuctionDeadline}</b> 前仍未补足，该订单将被取消，该拍品将重新上架拍卖
        </div>
      )}
    </div>
  );

  const statusBadge = (() => {
    if (b.status === 'paid')
      return <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid', fontWeight: 600, flexShrink: 0, marginTop: 2, ...statusStyle }}>{t('orders.statusPaid')}</span>;
    if (b.status === 'overdue')
      return (
        <Tooltip title={overdueTooltip} color={OVERDUE_C} overlayStyle={{ maxWidth: 280 }}>
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid', fontWeight: 600, flexShrink: 0, marginTop: 2, cursor: 'help', ...statusStyle }}>{t('bill.expense')}</span>
        </Tooltip>
      );
    if (b.status === 'pending_confirmation')
      return <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid', fontWeight: 600, flexShrink: 0, marginTop: 2, ...statusStyle }}>{t('bill.detail')}</span>;
    if (urgent)
      return (
        <Tooltip title={urgentTooltip} color={RED} overlayStyle={{ maxWidth: 280 }}>
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid', fontWeight: 600, flexShrink: 0, marginTop: 2, cursor: 'help', ...statusStyle }}>{t('bill.time')}</span>
        </Tooltip>
      );
    return <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid', fontWeight: 600, flexShrink: 0, marginTop: 2, ...statusStyle }}>{t('orders.statusPending')}</span>;
  })();

  const handlePay = async () => {
    setPaying(true);
    try { await onRequestPayment(b.orderId); } finally { setPaying(false); }
  };

  const feeRows = [
    { label: t('bill.amount'), val: `¥${b.hammerJpy.toLocaleString()} JPY`, sub: `${formatAmount(b.hammerCny)} CNY`, color: GOLD },
    { label: `${t('bill.type')} ${(b.serviceFeeRate * 100).toFixed(1)}%`, val: formatAmount(b.serviceFee), sub: t('bill.remark') },
    { label: t('inspection.title'), val: formatAmount(b.inspectionFee), sub: t('inspection.details') },
    { label: t('orders.logistics'), val: formatAmount(b.logisticsFee), sub: t('orders.shippingAddress') },
    { label: t('bill.balance'), val: formatAmount(b.totalCny), sub: t('bill.detail') },
    { label: t('deposit.frozen'), val: `-${formatAmount(b.depositCny)}`, sub: t('deposit.available'), color: INK3 },
  ];

  return (
    <div
      ref={focusRef}
      style={{
        background: '#fff',
        border: isFocused ? `2px solid ${GOLD}` : `1px solid ${BD2}`,
        borderRadius: 6,
        marginBottom: 16,
        overflow: 'hidden',
        boxShadow: isFocused
          ? '0 0 0 4px rgba(200,146,42,.15), 0 4px 20px rgba(200,146,42,.25)'
          : '0 2px 12px rgba(26,22,18,.08)',
        transition: 'box-shadow .3s, border-color .3s',
      }}
    >
      {/* Card header */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: INK4, fontFamily: 'monospace', letterSpacing: 1, marginBottom: 4 }}>
            {b.sessionName} · {b.endAt}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>{b.icon} {b.lotTitle}</div>
          <div style={{ fontSize: 11, color: INK4, marginTop: 3, fontFamily: 'monospace' }}>
            账单号 {b.id} · 委托 {b.mandateId}
          </div>
        </div>
        {statusBadge}
      </div>

      {/* Fee grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, padding: '0 20px' }}>
        {feeRows.map((row, i) => (
          <div key={i} style={{
            padding: '11px 0', borderBottom: `1px solid ${BD2}`,
            ...(i % 2 === 0 ? { paddingRight: 20, borderRight: `1px solid ${BD2}` } : { paddingLeft: 20 }),
          }}>
            <div style={{ fontSize: 11, color: INK4, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
              {row.label}
              {row.label.includes('%') && (
                <span style={{ fontSize: 10, padding: '1px 5px', background: 'rgba(200,146,42,.12)', borderRadius: 2 }}>
                  {(b.serviceFeeRate * 100).toFixed(1)}%
                </span>
              )}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: row.color || INK }}>
              {row.val}
            </div>
            {row.sub && <div style={{ fontSize: 11, color: INK4, marginTop: 2 }}>{row.sub}</div>}
          </div>
        ))}
      </div>

      {/* FX note */}
      <div style={{
        padding: '10px 20px', background: 'rgba(200,146,42,.04)',
        borderTop: `1px solid #F0EDE8`, display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 12, color: INK3,
      }}>
        <span style={{ fontSize: 14 }}>ℹ️</span>
        <span style={{ flex: 1 }}>
          汇率以结拍日 17:00 JST 锁定（1 JPY = {b.jpyRate} CNY），不随后续付款日汇率波动。
        </span>
        <RateHistoryButton />
      </div>

      {/* Payment section */}
      {b.status === 'unpaid' && (
        <div style={{ padding: '14px 20px', background: '#F4F1EC', borderTop: `1px solid ${BD2}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: INK3, marginBottom: 3 }}>{t('bill.amount')}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700 }}>{formatAmount(b.balanceCny)}</div>
              <div style={{ fontSize: 12, color: INK3, marginTop: 2 }}>{t('deposit.frozen')} {formatAmount(b.depositCny)}</div>
              {b.payDeadline && (
                <div style={{ fontSize: 11, color: urgent ? RED : INK3, marginTop: 3 }}>
                  付款截止：{b.payDeadline}{urgent ? ` (${deadlineDays}天后)` : ''}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <button
                onClick={handlePay}
                disabled={paying}
                style={{
                  padding: '11px 28px', background: paying ? INK3 : GOLD, color: '#fff', border: 'none',
                  borderRadius: 3, fontSize: 14, fontWeight: 600, cursor: paying ? 'not-allowed' : 'pointer',
                }}
              >{paying ? t('common.loading') : t('payment.payNow')}</button>
              <div style={{ fontSize: 11, color: INK4, marginTop: 5 }}>{t('payment.paySuccess')}</div>
            </div>
          </div>
        </div>
      )}
      {b.status === 'pending_confirmation' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 20px', background: 'rgba(217,119,6,.06)',
          borderTop: '1px solid rgba(217,119,6,.2)',
        }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: AMB, fontWeight: 500 }}>{t('deposit.statusPending')}</div>
            {b.requestedAt && (
              <div style={{ fontSize: 11, color: INK4, marginTop: 2, fontFamily: 'monospace' }}>
                提交时间：{b.requestedAt}
              </div>
            )}
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: AMB }}>
            {formatAmount(b.balanceCny)}
          </div>
        </div>
      )}
      {b.status === 'paid' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '14px 20px', background: 'rgba(46,125,79,.10)',
          borderTop: '1px solid rgba(46,125,79,.15)',
        }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <span style={{ fontSize: 13, color: GRN, fontWeight: 500 }}>{t('orders.statusPaid')}</span>
          <span style={{ fontSize: 11, color: GRN, fontFamily: 'monospace', marginLeft: 'auto' }}>{b.paidAt}</span>
        </div>
      )}
      {b.status === 'overdue' && (
        <div style={{
          padding: '14px 20px',
          background: 'rgba(153,27,27,.06)',
          borderTop: '1px solid rgba(153,27,27,.2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 18 }}>🚨</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: OVERDUE_C, fontWeight: 500 }}>{t('bill.expense')}</div>
              <div style={{ fontSize: 11, color: INK4, marginTop: 2 }}>
                需补足余额 {formatAmount(b.balanceCny)} 及滞纳金后方可交付
              </div>
              {b.reAuctionDeadline && (
                <div style={{ fontSize: 11, color: OVERDUE_C, marginTop: 3, opacity: .8 }}>
                  ⚠️ {b.reAuctionDeadline} 前仍未补足，该订单将被取消，该拍品将重新上架拍卖
                </div>
              )}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: OVERDUE_C }}>
              {formatAmount(b.balanceCny)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
